import { Injectable, computed, signal } from '@angular/core';
import { DomainInfo } from '@fy-web-2/data-access-tenant';
import { Theme } from '@fy-web-2/shared-util';

export type TenantStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface TenantSnapshot {
  readonly status: TenantStatus;
  readonly theme: Theme | null;
  readonly logoUrl: string | null;
  readonly brandName: string | null;
  readonly faviconUrl: string | null;
  readonly loginBackgroundUrl: string | null;
  readonly raw: DomainInfo | null;
  readonly error: string | null;
}

const initialState: TenantSnapshot = {
  status: 'idle',
  theme: null,
  logoUrl: null,
  brandName: null,
  faviconUrl: null,
  loginBackgroundUrl: null,
  raw: null,
  error: null,
};

/**
 * Signal-based tenant store. Holds branding + theme state derived from
 * `domain-data`. Mutators are called by `TenantFacade` only; UI reads via the
 * read-only signals.
 */
@Injectable({ providedIn: 'root' })
export class TenantStore {
  private readonly state = signal<TenantSnapshot>(initialState);

  readonly status = computed(() => this.state().status);
  readonly theme = computed(() => this.state().theme);
  readonly logoUrl = computed(() => this.state().logoUrl);
  readonly brandName = computed(() => this.state().brandName);
  readonly faviconUrl = computed(() => this.state().faviconUrl);
  readonly loginBackgroundUrl = computed(() => this.state().loginBackgroundUrl);
  readonly raw = computed(() => this.state().raw);
  readonly error = computed(() => this.state().error);

  readonly isReady = computed(() => this.state().status === 'ready');

  setLoading(): void {
    this.state.update((s) => ({ ...s, status: 'loading', error: null }));
  }

  setReady(snapshot: Omit<TenantSnapshot, 'status' | 'error'>): void {
    this.state.set({ ...snapshot, status: 'ready', error: null });
  }

  setError(message: string): void {
    this.state.update((s) => ({ ...s, status: 'error', error: message }));
  }

  reset(): void {
    this.state.set(initialState);
  }
}
